// First-party, privacy-conscious analytics for the Pendli landing pages.
// No third-party scripts, no fingerprinting, no IP storage — just an anonymous
// random visitor/session id in localStorage plus the acquisition context.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VID_KEY = "pendli_vid";
const SID_KEY = "pendli_sid";
const SID_TS_KEY = "pendli_sid_ts";
const ATTR_KEY = "pendli_attr";
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 min inactivity = new session

const PAGE_BY_PATH = {
  "/": "home",
  "/huseier": "huseier",
  "/pendler": "pendler",
  "/kontakt": "kontakt",
  "/om-oss": "om-oss",
  "/personvern": "personvern",
  "/vilkar": "vilkar",
};

const uid = () =>
  (window.crypto?.randomUUID?.() ||
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`);

const ls = {
  get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } },
};

export const pageFromPath = (path) => PAGE_BY_PATH[path] || "other";

function detectDevice() {
  const ua = navigator.userAgent || "";
  const w = window.innerWidth || 1024;
  if (/iPad|Tablet|PlayBook|Silk/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))) return "tablet";
  if (/Mobi|iPhone|iPod|Android.*Mobile|Windows Phone/i.test(ua) || w < 768) return "mobile";
  if (w >= 768 && w < 1024 && "ontouchstart" in window) return "tablet";
  return "desktop";
}

const ORGANIC_HOSTS = /(google|bing|duckduckgo|yahoo|ecosia|qwant|baidu|yandex)\./i;

function readAttributionFromUrl() {
  const p = new URLSearchParams(window.location.search);
  const utm = {
    utm_source: p.get("utm_source") || null,
    utm_medium: p.get("utm_medium") || null,
    utm_campaign: p.get("utm_campaign") || null,
    utm_content: p.get("utm_content") || null,
  };
  let referrer = null;
  try {
    if (document.referrer) {
      const host = new URL(document.referrer).hostname.replace(/^www\./, "");
      if (host && host !== window.location.hostname.replace(/^www\./, "")) referrer = host;
    }
  } catch { /* ignore malformed referrer */ }

  let source_type = "direct";
  if (utm.utm_source) source_type = "campaign";
  else if (referrer && ORGANIC_HOSTS.test(referrer)) source_type = "organic";
  else if (referrer) source_type = "referral";

  return {
    ...utm,
    referrer,
    source_type,
    device: detectDevice(),
    landing_page: pageFromPath(window.location.pathname),
  };
}

function newSession() {
  const sid = uid();
  ls.set(SID_KEY, sid);
  ls.set(SID_TS_KEY, String(Date.now()));
  // A session keeps the acquisition source it started with, even after the
  // visitor navigates on to the registration form (UTMs drop out of the URL).
  const attr = readAttributionFromUrl();
  ls.set(ATTR_KEY, JSON.stringify(attr));
  return { sid, attr, isNew: true };
}

function identity() {
  let vid = ls.get(VID_KEY);
  const isNewVisitor = !vid;
  if (!vid) {
    vid = uid();
    ls.set(VID_KEY, vid);
  }

  const sid = ls.get(SID_KEY);
  const ts = Number(ls.get(SID_TS_KEY) || 0);
  const expired = !sid || !ts || Date.now() - ts > SESSION_TIMEOUT_MS;

  let session;
  if (expired) {
    session = newSession();
  } else {
    ls.set(SID_TS_KEY, String(Date.now()));
    let attr = null;
    try { attr = JSON.parse(ls.get(ATTR_KEY) || "null"); } catch { attr = null; }
    // A mid-session UTM link (e.g. a QR scan) starts a fresh campaign context.
    const fresh = readAttributionFromUrl();
    if (fresh.utm_source && fresh.utm_source !== attr?.utm_source) {
      attr = fresh;
      ls.set(ATTR_KEY, JSON.stringify(attr));
    }
    session = { sid, attr: attr || fresh, isNew: false };
  }

  return { visitor_id: vid, is_new_visitor: isNewVisitor, session };
}

/** Acquisition context to attach to a lead registration. */
export function getAttribution() {
  const { visitor_id, session } = identity();
  return { visitor_id, session_id: session.sid, ...(session.attr || {}) };
}

const sent = new Set();

async function send(type, extra = {}, dedupeKey = null) {
  if (dedupeKey) {
    if (sent.has(dedupeKey)) return;
    sent.add(dedupeKey);
  }
  const { visitor_id, is_new_visitor, session } = identity();
  const body = {
    type,
    client_event_id: uid(),
    visitor_id,
    session_id: session.sid,
    is_new_visitor,
    is_new_session: session.isNew,
    ...(session.attr || {}),
    ...extra,
  };
  try {
    await fetch(`${API}/analytics/events`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch (err) {
    // Analytics must never break the page.
    console.debug("analytics event dropped:", err?.message || err);
  }
}

let lastPageViewPath = null;

/** One page view per navigation (React re-renders / StrictMode double effects are ignored). */
export function trackPageView(path) {
  if (lastPageViewPath === path) return;
  lastPageViewPath = path;
  const page = pageFromPath(path);
  const journey = page === "huseier" ? "huseier" : page === "pendler" ? "pendler" : null;
  send("page_view", { page, journey });
}

const lastClick = new Map();

/** journey: "huseier" | "pendler", location: "hero" | "nav" | "banner" | ... */
export function trackCtaClick(journey, location) {
  const key = `${journey}:${location}`;
  const now = Date.now();
  if (now - (lastClick.get(key) || 0) < 1000) return; // ignore double taps
  lastClick.set(key, now);
  send("cta_click", {
    journey,
    cta_location: location,
    page: pageFromPath(window.location.pathname),
  });
}

/** Fired when a visitor starts filling in a registration form — once per session/journey. */
export function trackRegistrationStart(journey) {
  const { session } = identity();
  send(
    "registration_start",
    { journey, page: pageFromPath(window.location.pathname) },
    `start:${session.sid}:${journey}`
  );
}
